import React, { useMemo, useState } from 'react';
import { PermissionsCheck } from '../../Components/Auth/PermissionsCheck';
import { Button, ButtonMode } from '../../Components/Button';
import { DurationPicker, DurationPickerTimeLength } from '../../Components/DurationPicker/DurationPicker';
import { FloatingLinearProgress } from '../../Components/FloatingLinearProgress';
import { CheckIcon, CloseIcon, EditIcon, TrashCanIcon } from '../../Components/Icons';
import { MaskedTextField } from '../../Components/MaskedTextField';
import { SearchInput } from '../../Components/SearchInput';
import { TimeUnits } from '../../Config/MedicationConfig';
import { MedicationListEntriesSearchResult } from '../../Contracts/MedicationListEntriesSearchResult.Response';
import { MedicationListStatus } from '../../Contracts/MedicationListsSearchFilter.Request';
import { MedicationsPermissions } from '../../MedicationsPermissions';
import { numberGreaterThanZeroInputFilter } from '../../Shared/InputFilters';
import { DeepReadonly } from '../../Shared/Types';
import { iconLight, primary } from '../../Styles/Colours';
import styles from './MedicationListEntries.module.scss';
import { MedicationListErrors } from './MedicationListErrors';
import { EntryLoadingState, MedListState } from './UseMedicationListState';
import { Tooltip } from '@material-ui/core';

function createBaseStateFromProps(it: MedicationListEntriesSearchResult) {
    return {
        defaultPackAmount: it.defaultPackAmount ? it.defaultPackAmount.toString() : '',
        defaultDosageInstructions: it.defaultDosageInstructions ? it.defaultDosageInstructions : '',
        defaultDuration: it.defaultDuration
            ? {
                  number: it.defaultDuration.number.toString(),
                  timeUnits: it.defaultDuration.timeUnits
              }
            : {
                  number: '',
                  timeUnits: TimeUnits.Days
              }
    };
}

const truncateAndTooltip = (text: string): JSX.Element => {
    return (
        <div className={styles.truncate}>
            <Tooltip title={text} placement={'bottom-start'}>
                <span>{text}</span>
            </Tooltip>
        </div>
    );
};

function DisplayRow(props: {
    it: MedicationListEntriesSearchResult;
    medListState: DeepReadonly<MedListState>;
    onEditClick(it: MedicationListEntriesSearchResult): void;
    onDeleteClick(it: MedicationListEntriesSearchResult): void;
}) {
    return (
        <div className={styles.row} data-test-id='display-row' data-test-name={props.it.medication?.name?.slice(0, 20)}>
            {truncateAndTooltip(props.it.medication.name)}
            <div>{props.it.defaultPackAmount}</div>
            <div>
                {props.it.defaultDuration &&
                props.it.defaultDuration.number &&
                Number(props.it.defaultDuration.number) > 0
                    ? `${props.it.defaultDuration.number} ${props.it.defaultDuration.timeUnits}`
                    : ''}
            </div>
            {truncateAndTooltip(props.it.defaultDosageInstructions)}
            {!props.medListState.editRowId &&
                props.medListState.medicationList &&
                props.medListState.medicationList.status === MedicationListStatus.Draft && (
                    <div className={styles.buttonContainer}>
                        <PermissionsCheck permission={MedicationsPermissions.WriteMedicationLists}>
                            <Button
                                mode={ButtonMode.text}
                                noShadowOnHover
                                iconOnly
                                tooltip={'Edit'}
                                testId={'medication-list-item-edit'}
                                onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    props.onEditClick(props.it);
                                }}
                            >
                                <EditIcon color={iconLight} size={'20'} noPadding />
                            </Button>
                            <Button
                                mode={ButtonMode.text}
                                noShadowOnHover
                                iconOnly
                                tooltip={'Delete'}
                                testId={'medication-list-item-delete'}
                                onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    props.onDeleteClick(props.it);
                                }}
                            >
                                <TrashCanIcon color={iconLight} size={'20'} noPadding />
                            </Button>
                        </PermissionsCheck>
                    </div>
                )}
        </div>
    );
}

function EditRow(props: {
    it: MedicationListEntriesSearchResult;
    medListState: DeepReadonly<MedListState>;
    onCancel(): void;
    onSaveMedication(newData: MedicationListEntriesSearchResult): void;
}) {
    const [rowData, setRowData] = useState<{
        defaultPackAmount: string;
        defaultDosageInstructions: string;
        defaultDuration: DurationPickerTimeLength;
    }>(createBaseStateFromProps(props.it));

    const handleSaveClick = () => {
        const newData: MedicationListEntriesSearchResult = {
            ...props.it,
            defaultDuration: rowData.defaultDuration.number
                ? {
                      number: Number(rowData.defaultDuration.number),
                      timeUnits: rowData.defaultDuration.timeUnits
                  }
                : null,
            defaultDosageInstructions: rowData.defaultDosageInstructions,
            defaultPackAmount: rowData.defaultPackAmount ? Number(rowData.defaultPackAmount) : undefined
        };

        props.onSaveMedication(newData);
    };

    return (
        <div className={styles.row} data-test-id='edit-row' data-test-name={props.it.medication?.name?.slice(0, 20)}>
            <div>{props.it.medication?.name}</div>
            <div>
                <MaskedTextField
                    value={rowData.defaultPackAmount ?? ''}
                    testId='pack-size'
                    disabled={props.medListState.updatingRow}
                    onChange={value => {
                        setRowData(data => ({
                            ...data,
                            defaultPackAmount: value
                        }));
                    }}
                    onClear={() => {
                        setRowData(data => ({
                            ...data,
                            defaultPackAmount: ''
                        }));
                    }}
                    hideWarningEmptyHelperText
                    filterFunction={numberGreaterThanZeroInputFilter}
                />
            </div>
            <div>
                <DurationPicker
                    timeLength={rowData.defaultDuration}
                    onUpdateTimeLength={timeLength => {
                        setRowData(data => ({
                            ...data,
                            defaultDuration: timeLength
                        }));
                    }}
                    disabled={props.medListState.updatingRow}
                    inputHeight={34}
                    hideLabel
                    hideWarningEmptyHelperText
                    testId='course-duration'
                />
            </div>
            <div>
                <MaskedTextField
                    value={rowData.defaultDosageInstructions ?? ''}
                    testId='dosage-instruction'
                    disabled={props.medListState.updatingRow}
                    onChange={value => {
                        setRowData(data => ({
                            ...data,
                            defaultDosageInstructions: value
                        }));
                    }}
                    onClear={() => {
                        setRowData(data => ({
                            ...data,
                            defaultDosageInstructions: ''
                        }));
                    }}
                    hideWarningEmptyHelperText
                />
            </div>
            {!props.medListState.updatingRow && (
                <div className={styles.buttonContainer}>
                    <Button
                        mode={ButtonMode.text}
                        noShadowOnHover
                        iconOnly
                        tooltip={'Save'}
                        testId={'medication-list-item-save'}
                        onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSaveClick();
                        }}
                    >
                        <CheckIcon color={primary} size={'20'} noPadding />
                    </Button>
                    <PermissionsCheck permission={MedicationsPermissions.WriteMedicationLists}>
                        <Button
                            mode={ButtonMode.text}
                            noShadowOnHover
                            iconOnly
                            tooltip={'Cancel'}
                            testId={'medication-list-item-cancel'}
                            onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                props.onCancel();
                            }}
                        >
                            <CloseIcon color={iconLight} size={'20'} noPadding />
                        </Button>
                    </PermissionsCheck>
                </div>
            )}
        </div>
    );
}

export const MedicationListEntries = (props: {
    medListState: DeepReadonly<MedListState>;
    onAddAction(): void;
    onSaveMedication(newData: MedicationListEntriesSearchResult): void;
    onRowEditStart(row: MedicationListEntriesSearchResult): void;
    onDeleteMedication(row: MedicationListEntriesSearchResult): void;
    onRowEditCancelled(): void;
    onSearch(value: string): void;
}) => {
    const entries = useMemo(() => {
        return props.medListState.entries.map(it =>
            props.medListState.editRowId === it.id ? (
                <EditRow
                    medListState={props.medListState}
                    key={it.id}
                    it={it}
                    onSaveMedication={props.onSaveMedication}
                    onCancel={props.onRowEditCancelled}
                />
            ) : (
                <DisplayRow
                    medListState={props.medListState}
                    key={it.id}
                    it={it}
                    onEditClick={props.onRowEditStart}
                    onDeleteClick={props.onDeleteMedication}
                />
            )
        );
    }, [
        props.medListState.editRowId,
        props.medListState.entries,
        props.medListState.medicationList?.status,
        props.medListState.updatingRow
    ]);

    const hideSearchBar = !props.medListState.queryUsed && props.medListState.entries.length === 0;

    return (
        <div className={styles.table}>
            <FloatingLinearProgress display={props.medListState.entryLoadingState === EntryLoadingState.Refreshing} />
            <div className={styles.tableHeader}>
                <div>Medication name</div>
                <div>No. of packs</div>
                <div>Course duration</div>
                <div>Dosage instruction</div>
            </div>
            {!hideSearchBar && (
                <div className={styles.search}>
                    <SearchInput
                        testId='entry-list-search'
                        value={props.medListState.query}
                        fullWidth
                        onChange={e => {
                            props.onSearch(e.target.value);
                        }}
                        onClear={() => {
                            props.onSearch('');
                        }}
                        placeholder='Search by medication name...'
                    />
                </div>
            )}
            {entries}
            <MedicationListErrors
                hasEntries={props.medListState.entries.length > 0}
                hasQuery={!!props.medListState.queryUsed}
                hasPublished={props.medListState.medicationList?.status == MedicationListStatus.Published}
                onAddAction={props.onAddAction}
            />
        </div>
    );
};
